import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2, DollarSign, Building, Users, FileText, CheckCircle, XCircle,
  AlertTriangle, Clock, CreditCard, Settings2, TrendingUp,
  Receipt, Search, Filter, RefreshCw, Play, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_BADGES = {
  trial: { label: 'Trial', className: 'bg-sky-100 text-sky-700 border-sky-200' },
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-700 border-red-200' },
  deactivated: { label: 'Deactivated', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  read_only: { label: 'Read Only', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  warning: { label: 'Warning', className: 'bg-orange-100 text-orange-700 border-orange-200' },
};

const INV_STATUS_BADGES = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
  overdue: { label: 'Overdue', className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500' },
};

const OVERDUE_OPTIONS = [
  { value: 'deactivate', label: 'Deactivate Account', desc: 'Block all access for the office' },
  { value: 'read_only', label: 'Read-Only Mode', desc: 'Users can login but cannot perform actions' },
  { value: 'warning_only', label: 'Warning Banner', desc: 'Show a warning but keep full access' },
];

const CURRENCIES = ['USD', 'TZS', 'KES', 'UGX', 'EUR', 'GBP', 'ZAR', 'NGN', 'GHS', 'RWF', 'ETB', 'INR', 'AED', 'CAD', 'AUD'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const fmtMoney = (amount, currency) => {
  if (amount == null) return '—';
  const cur = currency || 'USD';
  return `${cur} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const BillingManagement = () => {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [configs, setConfigs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [invFilter, setInvFilter] = useState('all');

  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [configForm, setConfigForm] = useState({
    subscription_fee: '', per_user_fee: '', free_user_threshold: '10',
    currency: 'USD', overdue_action: 'warning_only', payment_grace_days: '10', notes: '',
  });

  const [payForm, setPayForm] = useState({ paid_amount: '', payment_reference: '', notes: '' });

  const { getAuthHeaders } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [dashRes, cfgRes, invRes] = await Promise.all([
        fetch(`${API}/api/billing/dashboard`, { headers }),
        fetch(`${API}/api/billing/configs`, { headers }),
        fetch(`${API}/api/billing/invoices`, { headers }),
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (cfgRes.ok) setConfigs(await cfgRes.json());
      if (invRes.ok) setInvoices(await invRes.json());
    } catch { toast.error('Failed to load billing data'); }
    finally { setLoading(false); }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openConfig = (cfg) => {
    setSelectedConfig(cfg);
    setConfigForm({
      subscription_fee: cfg.subscription_fee || '',
      per_user_fee: cfg.per_user_fee || '',
      free_user_threshold: cfg.free_user_threshold ?? 10,
      currency: cfg.currency || 'USD',
      overdue_action: cfg.overdue_action || 'warning_only',
      payment_grace_days: cfg.payment_grace_days ?? 10,
      notes: cfg.notes || '',
    });
    setShowConfigDialog(true);
  };

  const saveConfig = async () => {
    setSubmitting(true);
    try {
      const body = {
        subscription_fee: parseFloat(configForm.subscription_fee) || 0,
        per_user_fee: parseFloat(configForm.per_user_fee) || 0,
        free_user_threshold: parseInt(configForm.free_user_threshold) || 10,
        currency: configForm.currency,
        overdue_action: configForm.overdue_action,
        payment_grace_days: parseInt(configForm.payment_grace_days) || 10,
        notes: configForm.notes,
      };
      const res = await fetch(`${API}/api/billing/config/${selectedConfig.office_id}`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { toast.success('Billing config saved'); setShowConfigDialog(false); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  const activateBilling = async (officeId) => {
    const res = await fetch(`${API}/api/billing/config/${officeId}/activate`, { method: 'POST', headers: getAuthHeaders() });
    if (res.ok) { toast.success('Billing activated'); fetchAll(); } else toast.error('Failed to activate');
  };

  const reactivateOffice = async (officeId) => {
    const res = await fetch(`${API}/api/billing/config/${officeId}/reactivate`, { method: 'POST', headers: getAuthHeaders() });
    if (res.ok) { toast.success('Office reactivated'); fetchAll(); } else toast.error('Failed to reactivate');
  };

  const openPay = (inv) => {
    setSelectedInvoice(inv);
    setPayForm({ paid_amount: inv.total_amount, payment_reference: '', notes: '' });
    setShowPayDialog(true);
  };

  const markPaid = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/billing/invoices/${selectedInvoice.id}/status`, {
        method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'paid', paid_amount: parseFloat(payForm.paid_amount), payment_reference: payForm.payment_reference, notes: payForm.notes }),
      });
      if (res.ok) { toast.success('Invoice marked as paid — account reactivated'); setShowPayDialog(false); fetchAll(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  const cancelInvoice = async (invId) => {
    const res = await fetch(`${API}/api/billing/invoices/${invId}/status`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'cancelled' }),
    });
    if (res.ok) { toast.success('Invoice cancelled'); fetchAll(); } else toast.error('Failed');
  };

  const downloadPdf = async (invId, invNumber) => {
    try {
      const res = await fetch(`${API}/api/billing/invoices/${invId}/pdf`, { headers: getAuthHeaders() });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else toast.error('Failed to download PDF');
    } catch { toast.error('Network error'); }
  };

  const filteredConfigs = configs.filter(c =>
    !search || c.office_name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredInvoices = invoices.filter(inv => {
    const matchSearch = !search || inv.office_name?.toLowerCase().includes(search.toLowerCase()) || inv.invoice_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = invFilter === 'all' || inv.status === invFilter;
    return matchSearch && matchStatus;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="billing-loading">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="billing-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Billing Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure office billing and manage invoices</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Offices', value: dashboard.total_offices, icon: Building, color: 'text-foreground' },
          { label: 'On Trial', value: dashboard.trial_count, icon: Clock, color: 'text-sky-600' },
          { label: 'Active', value: dashboard.active_count, icon: CheckCircle, color: 'text-emerald-600' },
          { label: 'Overdue', value: dashboard.overdue_count, icon: AlertTriangle, color: 'text-red-600' },
          { label: 'Revenue', value: fmtMoney(dashboard.total_revenue, dashboard.primary_currency), icon: TrendingUp, color: 'text-emerald-600', isText: true },
          { label: 'Pending', value: fmtMoney(dashboard.pending_amount, dashboard.primary_currency), icon: CreditCard, color: 'text-amber-600', isText: true },
          { label: 'Invoices', value: dashboard.total_invoices, icon: FileText, color: 'text-foreground' },
        ].map(s => (
          <Card key={s.label} className="p-3" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center gap-2">
              <s.icon className={`w-4 h-4 ${s.color} shrink-0`} />
              <div className="min-w-0">
                <p className={`font-bold truncate ${s.isText ? 'text-sm' : 'text-xl'}`}>{s.value ?? 0}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Office Billing</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        {/* ── Office Billing Tab ── */}
        <TabsContent value="overview" className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search offices..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="office-search" />
          </div>

          <div className="space-y-3">
            {filteredConfigs.map(cfg => {
              const sb = STATUS_BADGES[cfg.billing_status] || STATUS_BADGES.active;
              return (
                <Card key={cfg.office_id} className="p-4 hover:shadow-sm transition-shadow" data-testid={`billing-card-${cfg.office_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 rounded-lg bg-primary/10 shrink-0"><Building className="w-5 h-5 text-primary" /></div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm truncate">{cfg.office_name}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge variant="outline" className={`text-[10px] ${sb.className}`}>{sb.label}</Badge>
                          {cfg.is_trial && <span className="text-[10px] text-muted-foreground">Trial ends: {fmtDate(cfg.trial_end_date)}</span>}
                          {!cfg.is_trial && cfg.next_billing_date && <span className="text-[10px] text-muted-foreground">Next billing: {fmtDate(cfg.next_billing_date)}</span>}
                          <span className="text-[10px] text-muted-foreground">
                            {cfg.currency} {cfg.subscription_fee || 0}/mo + {cfg.currency} {cfg.per_user_fee || 0}/user
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {cfg.is_trial && (
                        <Button variant="outline" size="sm" onClick={() => activateBilling(cfg.office_id)} data-testid={`activate-${cfg.office_id}`}>
                          <Play className="w-3 h-3 mr-1" /> Activate
                        </Button>
                      )}
                      {(cfg.billing_status === 'deactivated' || cfg.billing_status === 'read_only' || cfg.billing_status === 'warning') && (
                        <Button variant="outline" size="sm" onClick={() => reactivateOffice(cfg.office_id)} data-testid={`reactivate-${cfg.office_id}`}>
                          <RefreshCw className="w-3 h-3 mr-1" /> Reactivate
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openConfig(cfg)} data-testid={`configure-${cfg.office_id}`}>
                        <Settings2 className="w-3 h-3 mr-1" /> Configure
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
            {filteredConfigs.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Building className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No offices found</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Invoices Tab ── */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search invoices..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="invoice-search" />
            </div>
            <Select value={invFilter} onValueChange={setInvFilter}>
              <SelectTrigger className="w-[130px]" data-testid="invoice-filter">
                <Filter className="w-3.5 h-3.5 mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Invoice</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Office</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Period</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Amount</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Due</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => {
                  const isb = INV_STATUS_BADGES[inv.status] || INV_STATUS_BADGES.draft;
                  return (
                    <tr key={inv.id} className="border-t hover:bg-muted/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="px-4 py-3">{inv.office_name}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmtMoney(inv.total_amount, inv.currency)}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={`text-[10px] ${isb.className}`}>{isb.label}</Badge></td>
                      <td className="px-4 py-3 text-xs">{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`download-${inv.id}`}>
                            <Download className="w-3 h-3 mr-1" /> PDF
                          </Button>
                          {(inv.status === 'sent' || inv.status === 'overdue') && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600" onClick={() => openPay(inv)} data-testid={`pay-${inv.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => cancelInvoice(inv.id)}>
                                <XCircle className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredInvoices.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No invoices found</p>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Config Dialog ── */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle>Billing Configuration</DialogTitle>
            <DialogDescription>{selectedConfig?.office_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Subscription Fee / Month</Label>
                <Input type="number" min="0" step="0.01" value={configForm.subscription_fee} onChange={e => setConfigForm(p => ({ ...p, subscription_fee: e.target.value }))} data-testid="cfg-sub-fee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Per User Fee (above threshold)</Label>
                <Input type="number" min="0" step="0.01" value={configForm.per_user_fee} onChange={e => setConfigForm(p => ({ ...p, per_user_fee: e.target.value }))} data-testid="cfg-user-fee" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Free User Threshold</Label>
                <Input type="number" min="0" value={configForm.free_user_threshold} onChange={e => setConfigForm(p => ({ ...p, free_user_threshold: e.target.value }))} data-testid="cfg-threshold" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Currency</Label>
                <Select value={configForm.currency} onValueChange={v => setConfigForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger data-testid="cfg-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Overdue Action</Label>
                <Select value={configForm.overdue_action} onValueChange={v => setConfigForm(p => ({ ...p, overdue_action: v }))}>
                  <SelectTrigger data-testid="cfg-overdue"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OVERDUE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">{OVERDUE_OPTIONS.find(o => o.value === configForm.overdue_action)?.desc}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Payment Grace Days</Label>
                <Input type="number" min="1" value={configForm.payment_grace_days} onChange={e => setConfigForm(p => ({ ...p, payment_grace_days: e.target.value }))} data-testid="cfg-grace" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={configForm.notes} onChange={e => setConfigForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancel</Button>
            <Button onClick={saveConfig} disabled={submitting} data-testid="save-config-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Pay Dialog ── */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="max-w-sm" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>{selectedInvoice?.invoice_number} — {selectedInvoice?.office_name}</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground bg-emerald-50 border border-emerald-200 rounded-lg p-2">
            Marking this invoice as paid will automatically reactivate the office account if it was deactivated or restricted.
          </p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Amount Paid</Label>
              <Input type="number" min="0" step="0.01" value={payForm.paid_amount} onChange={e => setPayForm(p => ({ ...p, paid_amount: e.target.value }))} data-testid="pay-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Payment Reference</Label>
              <Input value={payForm.payment_reference} onChange={e => setPayForm(p => ({ ...p, payment_reference: e.target.value }))} placeholder="e.g. TXN-12345" data-testid="pay-ref" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={payForm.notes} onChange={e => setPayForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancel</Button>
            <Button onClick={markPaid} disabled={submitting} data-testid="confirm-pay-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingManagement;
